<!--
 * @Author: LiZhiWei
 * @Date: 2026-01-13 15:41:49
 * @LastEditors: LiZhiWei
 * @LastEditTime: 2026-01-29 14:29:36
 * @Description: 
-->
<!--
 * @Author: LiZhiWei
 * @Description: AppHeader
-->
<template>
  <header class="landing-header" :class="{ 'is-scrolled': y > 0 }">
    <div class="landing-container header-content">
      <NuxtLink to="/" aria-label="首页">
        <i class="header-logo" aria-hidden="true"></i>
      </NuxtLink>

      <nav class="hidden sm:flex items-center gap-52px text-16px text-#334155 h-full">
        <template v-for="nav in navLinks" :key="nav.title">
          <!-- Normal Link -->
          <NuxtLink v-if="nav.link" :to="nav.link" class="nav-link flex items-center">
            {{ nav.title }}
          </NuxtLink>

          <!-- Product Dropdown -->
          <div
            v-else-if="nav.type === 'product'"
            class="group h-full flex items-center"
            role="button"
            aria-haspopup="true"
            :aria-expanded="isProductsMenuOpen"
            tabindex="0"
            @mouseenter="isProductsMenuOpen = true"
            @mouseleave="isProductsMenuOpen = false"
            @keydown.enter="isProductsMenuOpen = !isProductsMenuOpen"
            @keydown.space.prevent="isProductsMenuOpen = !isProductsMenuOpen"
          >
            <NuxtLink class="nav-link flex items-center">{{ nav.title }}</NuxtLink>
            <m-header-product-menu
              v-show="isProductsMenuOpen"
              @close="isProductsMenuOpen = false"
            />
          </div>

          <!-- Solution Dropdown -->
          <div
            v-else-if="nav.type === 'solution'"
            class="group h-full flex items-center"
            role="button"
            aria-haspopup="true"
            :aria-expanded="isSolutionsMenuOpen"
            tabindex="0"
            @mouseenter="isSolutionsMenuOpen = true"
            @mouseleave="isSolutionsMenuOpen = false"
            @keydown.enter="isSolutionsMenuOpen = !isSolutionsMenuOpen"
            @keydown.space.prevent="isSolutionsMenuOpen = !isSolutionsMenuOpen"
          >
            <NuxtLink class="nav-link flex items-center">{{ nav.title }}</NuxtLink>
            <m-header-solution-menu
              v-show="isSolutionsMenuOpen"
              @close="isSolutionsMenuOpen = false"
            />
          </div>
        </template>
      </nav>

      <div class="flex items-center gap-30px lt-sm:hidden">
        <i class="i-custom-head-phone h-28px w-199px" aria-label="联系电话：400-600-7709"></i>
        <button class="btn-primary btn-apply" @click="openConsultation">
          <span class="lh-20px">免费咨询</span>
        </button>
      </div>

      <button
        class="menu-toggle-btn"
        :aria-label="isMobileMenuOpen ? '关闭菜单' : '打开菜单'"
        :aria-expanded="isMobileMenuOpen"
        @click="isMobileMenuOpen = !isMobileMenuOpen"
      >
        <i v-if="!isMobileMenuOpen" class="i-custom-menu menu-icon" aria-hidden="true"></i>
        <i v-else class="i-ep-close menu-icon" aria-hidden="true"></i>
      </button>
    </div>
  </header>

  <!-- Mobile Menu Overlay -->
  <m-header-mobile-menu v-if="isMobileMenuOpen" @close="isMobileMenuOpen = false" />
</template>

<script setup lang="ts">
  import { navLinks } from '~/constants/navigation'

  const { openConsultation } = useConsultation()
  const { y } = useWindowScroll()

  const isMobileMenuOpen = ref(false)
  const isProductsMenuOpen = ref(false)
  const isSolutionsMenuOpen = ref(false)
</script>

<style scoped lang="scss">
  .landing-header {
    @apply fixed top-0 left-0 z-100 w-full bg-transparent transition-all duration-300 ease;
    @apply lt-sm:sticky;

    &:hover,
    &.is-scrolled {
      @apply bg-white/80 backdrop-blur-12px shadow-[0_4px_20px_rgba(0,0,0,0.05)] border-b border-white/10;
    }

    @screen lt-sm {
      @apply bg-white/80 backdrop-blur-12px shadow-[0_4px_20px_rgba(0,0,0,0.05)] border-b border-white/10;
    }
  }

  .nav-link {
    @apply relative py-8px text-[var(--hj-text-2)] transition-all duration-200 ease;

    &:hover,
    .group:hover & {
      @apply text-[var(--hj-primary)] font-600;
    }

    &::after {
      @apply content-[''] absolute bottom-0 left-1/2 w-0 h-3px bg-[var(--hj-primary)] transition-all duration-300 ease -translate-x-1/2;
    }

    &:hover::after,
    .group:hover &::after {
      @apply w-30px;
    }
  }

  .btn-primary {
    @apply bg-gradient-to-r from-[#779EFF] to-[#0A50FF] border-none outline-none hover:opacity-80;
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }

  .animate-fade-in-down {
    animation: fadeInDown 0.2s ease-out forwards;

    &::before {
      content: '';
      @apply absolute -top-10px left-0 w-full h-10px;
    }
  }

  .mobile-nav-item {
    @apply py-16px border-b border-gray-100 active:text-primary transition-colors;
  }

  .safe-area-bottom {
    padding-bottom: calc(24px + env(safe-area-inset-bottom));
  }

  /* Extracted Styles */
  .header-content {
    @apply flex items-center justify-between gap-16px h-80px;
    @apply lt-sm:h-128px lt-sm:px-24px;
  }

  .header-logo {
    @apply i-custom-logo w-160px h-32px;
    @apply lt-sm:w-213px lt-sm:h-64px;
  }

  .product-card {
    @apply flex gap-16px p-8px w-358px rounded-8px bg-#f6f8fd cursor-pointer transition-all duration-300 border border-transparent;
    @apply hover:border-gray-100 hover:shadow-sm;
  }

  .product-icon {
    @apply wh-106px flex-shrink-0;
  }

  .btn-apply {
    @apply text-white font-medium w-134px h-40px pf-sc-medium font-s-16px rounded-8px hidden sm:block;
  }

  .menu-toggle-btn {
    @apply sm:hidden text-#334155;
  }

  .menu-icon {
    @apply lt-sm:wh-48px;
  }

  .mobile-submenu-trigger {
    @apply flex items-center justify-between !border-none cursor-pointer;
  }

  .mobile-product-card {
    @apply flex items-center gap-12px p-12px rounded-8px bg-gray-50/80 border border-gray-100 transition-all duration-200;
    @apply active:bg-blue-50 active:border-blue-200;
  }

  .mobile-product-icon {
    @apply wh-40px flex-shrink-0;
    @apply lt-sm:wh-78px;
  }

  .mobile-solution-card {
    @apply text-#475569 bg-gray-50/80 border border-gray-100  flex items-center justify-start text-left transition-all duration-200 gap-8px;
    @apply active:bg-blue-50 active:border-blue-200 active:text-blue-600;
    @apply lt-sm:font-s-24px lt-sm:rounded-8px lt-sm:py-18px lt-sm:px-16px;
  }

  .mobile-solution-icon {
    @apply lt-sm:wh-48px flex-shrink-0;
  }
</style>
